/**
 * Gated HITL bridges for the structure list's per-structure actions:
 * rename, delete, duplicate, "replace another structure" and the Type
 * menu. Each mirrors the panel's own action and checks:
 *  - rename: the name field's rules (`validateStructureName`: 1–16
 *    printable ASCII, no backslash, unique); refused when approved;
 *  - delete: the delete confirmation dialog's action; refused when
 *    approved (the menu item is disabled then);
 *  - duplicate: not for markers or empty structures (menu rules);
 *  - replace: the TARGET's contours are overwritten by the SOURCE's;
 *    refused for an approved / marker target or an empty / marker source;
 *    one contour undo step on the target;
 *  - type: the Type menu's choices for the structure's category (no
 *    switching between contoured structure and marker), one EXTERNAL per
 *    set; refused when approved.
 * Apply re-checks (structure set, approval, name still free) and confirms
 * by reading the value back, because the existing viewer handlers report
 * problems only in the console. Pure/React-free; ports injected.
 */
import {
    AVAILABLE_ROI_TYPES,
    ROI_POINT_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { validateStructureName } from '~/utils/validation';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    resolveFailureToPlan,
    resolveOneStructure,
    toListed,
    type MutatingPlan,
    type MutatingToolCall,
} from '../core';
import {
    gatedChange,
    settle,
    type ChangeProposal,
} from '../core/changeProposal';
import type {
    InPlaceEditOutcome,
    MutationOutcome,
    StructureChangeOutcome,
} from '../../contract';
import {
    approvedReason,
    liveRoi,
    resolveAnyRoi,
    type LiveDeps,
} from './liveState';

export type StructureChangeProposal = ChangeProposal<StructureChangeOutcome>;
export type DuplicateProposal = ChangeProposal<MutationOutcome>;
export type ReplaceContoursProposal = ChangeProposal<InPlaceEditOutcome>;

export interface StructureManagementDeps extends LiveDeps {
    /** Structure volume now (cm³), read live; NaN when unknown. */
    volumeCm3(roiNumber: number): number;
    /** Contour edit version (moves on every contour change). */
    editVersion(roiNumber: number): number | undefined;
    rename(roiNumber: number, name: string): Promise<void>;
    deleteRoi(roiNumber: number): Promise<void>;
    duplicate(roiNumber: number): Promise<void>;
    replaceContours(sourceRoi: number, targetRoi: number): Promise<void>;
    setType(roiNumber: number, type: string): Promise<void>;
}

type Plan<O> = MutatingPlan<ChangeProposal<O>>;

const proposal = <O>(toolCallId: string, p: ChangeProposal<O>): Plan<O> => ({
    kind: 'proposal',
    toolCallId,
    proposal: p,
});

const NOT_UNDOABLE = 'This cannot be undone.';

// ---------------------------------------------------------------- rename

export async function planRenameStructure(
    call: MutatingToolCall,
    deps: StructureManagementDeps
): Promise<Plan<StructureChangeOutcome>> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input);
    const r = resolveAnyRoi(call.toolCallId, o?.roi, deps);
    if (r.kind !== 'ok') {
        return r;
    }
    const { roiNumber, name } = r.roi;
    if (typeof o?.newName !== 'string') {
        return failedPlan(call.toolCallId, 'a new name is required');
    }
    const newName = o.newName.trim();
    const otherNames = () =>
        (deps.liveSet()?.rois ?? [])
            .filter((x) => x.roiNumber !== roiNumber)
            .map((x) => x.name);
    const nameProblem = () => {
        const [ok, why] = validateStructureName(newName, otherNames());
        return ok ? undefined : (why ?? 'invalid name');
    };
    if (r.roi.isApproved) {
        return failedPlan(call.toolCallId, approvedReason(name));
    }
    if (newName === name) {
        return failedPlan(call.toolCallId, `'${name}' already has that name`);
    }
    const problem = nameProblem();
    if (problem) {
        return failedPlan(call.toolCallId, problem);
    }
    return proposal(
        call.toolCallId,
        gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'Rename structure',
            summary: `“${name}” → “${newName}”`,
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: () =>
                liveRoi(deps, roiNumber)?.isApproved
                    ? approvedReason(name)
                    : nameProblem(),
            run: async () => {
                await deps.rename(roiNumber, newName);
                await settle();
                if (liveRoi(deps, roiNumber)?.name !== newName) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not rename the structure',
                    };
                }
                return {
                    status: 'applied',
                    roiNumber,
                    roi: newName,
                    summary: `renamed '${name}' to '${newName}'`,
                };
            },
        })
    );
}

// ---------------------------------------------------------------- delete

export async function planDeleteStructure(
    call: MutatingToolCall,
    deps: StructureManagementDeps
): Promise<Plan<StructureChangeOutcome>> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const r = resolveAnyRoi(call.toolCallId, asRecord(call.input)?.roi, deps);
    if (r.kind !== 'ok') {
        return r;
    }
    const { roiNumber, name, isPointRoi } = r.roi;
    if (r.roi.isApproved) {
        return failedPlan(call.toolCallId, approvedReason(name));
    }
    const volume = r.facts?.volumeCm3;
    return proposal(
        call.toolCallId,
        gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'Delete structure',
            summary: `delete “${name}” (#${roiNumber})`,
            details: isPointRoi
                ? ['Marker']
                : volume !== undefined && Number.isFinite(volume)
                  ? [`Volume ${volume.toFixed(2)} cm³`]
                  : [],
            undoable: false,
            caution: `“${name}” and its contours are removed. ${NOT_UNDOABLE}`,
            currentContextId: deps.currentContextId,
            recheck: () =>
                liveRoi(deps, roiNumber)?.isApproved
                    ? approvedReason(name)
                    : undefined,
            run: async () => {
                await deps.deleteRoi(roiNumber);
                await settle();
                if (liveRoi(deps, roiNumber)) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not delete the structure',
                    };
                }
                return {
                    status: 'applied',
                    roiNumber,
                    roi: name,
                    summary: `deleted '${name}'`,
                };
            },
        })
    );
}

// ------------------------------------------------------------- duplicate

export async function planDuplicateStructure(
    call: MutatingToolCall,
    deps: StructureManagementDeps
): Promise<Plan<MutationOutcome>> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const roi = asRecord(call.input)?.roi;
    if (typeof roi !== 'string' || !roi) {
        return failedPlan(call.toolCallId, 'a structure name is required');
    }
    // Same rules as the menu: contoured, non-empty structures only.
    const resolved = resolveOneStructure(
        roi,
        deps.structures,
        deps.structures.map(toListed),
        'duplicate',
        'duplicate'
    );
    if (resolved.kind !== 'source') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }
    const { roiNumber, name, volumeCm3 } = resolved.source;
    const numbersBefore = new Set(
        (deps.liveSet()?.rois ?? []).map((x) => x.roiNumber)
    );
    return proposal(
        call.toolCallId,
        gatedChange<MutationOutcome>({
            toolCallId: call.toolCallId,
            title: 'Duplicate structure',
            summary: `copy “${name}” into a new structure`,
            details: [
                'The viewer names the copy and gives it a darker colour; ' +
                    'the original is not changed.',
            ],
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: () =>
                liveRoi(deps, roiNumber) ? undefined : `'${name}' is gone`,
            run: async () => {
                await deps.duplicate(roiNumber);
                await settle();
                const created = (deps.liveSet()?.rois ?? []).find(
                    (x) => !numbersBefore.has(x.roiNumber)
                );
                if (!created) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not create the copy',
                    };
                }
                const v = deps.volumeCm3(created.roiNumber);
                const newVolume = Number.isFinite(v) ? v : volumeCm3;
                return {
                    status: 'applied',
                    newRoiNumber: created.roiNumber,
                    newRoi: created.name,
                    volumeCm3: newVolume,
                    volumeDeltaCm3: newVolume - volumeCm3,
                };
            },
        })
    );
}

// --------------------------------------------------------------- replace

export async function planReplaceStructureContours(
    call: MutatingToolCall,
    deps: StructureManagementDeps
): Promise<Plan<InPlaceEditOutcome>> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input);
    if (typeof o?.source !== 'string' || !o.source) {
        return failedPlan(call.toolCallId, 'a source structure is required');
    }
    const src = resolveOneStructure(
        o.source,
        deps.structures,
        deps.structures.map(toListed),
        'contour copy',
        'copy'
    );
    if (src.kind !== 'source') {
        return resolveFailureToPlan(src, call.toolCallId);
    }
    const tgt = resolveAnyRoi(call.toolCallId, o.target, deps);
    if (tgt.kind !== 'ok') {
        return tgt;
    }
    const source = src.source;
    const target = tgt.roi;
    if (target.roiNumber === source.roiNumber) {
        return failedPlan(
            call.toolCallId,
            'the source and target are the same structure'
        );
    }
    if (target.isPointRoi) {
        return failedPlan(
            call.toolCallId,
            `'${target.name}' is a marker; contours cannot be copied onto it`
        );
    }
    if (target.isApproved) {
        return failedPlan(call.toolCallId, approvedReason(target.name));
    }
    const targetBefore = tgt.facts?.volumeCm3 ?? 0;
    const plannedVersion = deps.editVersion(target.roiNumber);
    return proposal(
        call.toolCallId,
        gatedChange<InPlaceEditOutcome>({
            toolCallId: call.toolCallId,
            title: 'Replace structure contours',
            summary:
                `overwrite “${target.name}” with the contours of ` +
                `“${source.name}”`,
            details: [
                `“${target.name}” keeps its name and colour; its current ` +
                    `${targetBefore.toFixed(2)} cm³ is replaced by ` +
                    `${source.volumeCm3.toFixed(2)} cm³.`,
                `“${source.name}” is not changed.`,
            ],
            undoable: true,
            currentContextId: deps.currentContextId,
            recheck: () => {
                if (liveRoi(deps, target.roiNumber)?.isApproved) {
                    return approvedReason(target.name);
                }
                return deps.editVersion(target.roiNumber) !== plannedVersion
                    ? `'${target.name}' was edited after this was proposed; ` +
                          're-issue the request'
                    : undefined;
            },
            run: async () => {
                await deps.replaceContours(source.roiNumber, target.roiNumber);
                await settle();
                if (deps.editVersion(target.roiNumber) === plannedVersion) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not replace the contours',
                    };
                }
                const after = deps.volumeCm3(target.roiNumber);
                return {
                    status: 'applied',
                    roiNumber: target.roiNumber,
                    roi: target.name,
                    volumeCm3: Number.isFinite(after)
                        ? after
                        : source.volumeCm3,
                    volumeDeltaCm3:
                        (Number.isFinite(after) ? after : source.volumeCm3) -
                        targetBefore,
                };
            },
        })
    );
}

// ------------------------------------------------------------------ type

/** The Type menu's choices for a structure's category. */
export function allowedTypes(isPointRoi: boolean): ReadonlyArray<string> {
    return isPointRoi
        ? ROI_POINT_TYPES
        : AVAILABLE_ROI_TYPES.filter((t) => !ROI_POINT_TYPES.includes(t));
}

export async function planSetStructureType(
    call: MutatingToolCall,
    deps: StructureManagementDeps
): Promise<Plan<StructureChangeOutcome>> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input);
    const r = resolveAnyRoi(call.toolCallId, o?.roi, deps);
    if (r.kind !== 'ok') {
        return r;
    }
    const { roiNumber, name, isPointRoi, type: fromType } = r.roi;
    const type = typeof o?.type === 'string' ? o.type : '';
    if (r.roi.isApproved) {
        return failedPlan(call.toolCallId, approvedReason(name));
    }
    const choices = allowedTypes(isPointRoi);
    if (!choices.includes(type)) {
        return failedPlan(
            call.toolCallId,
            `'${name}' is a ${isPointRoi ? 'marker' : 'contoured structure'}; ` +
                `its type can be one of: ${choices.join(', ')}`
        );
    }
    if (type === fromType) {
        return failedPlan(call.toolCallId, `'${name}' is already ${type}`);
    }
    const externalTaken = () =>
        type === RoiInterpretedType.EXTERNAL
            ? (deps.liveSet()?.rois ?? []).find(
                  (x) =>
                      x.roiNumber !== roiNumber &&
                      x.type === RoiInterpretedType.EXTERNAL
              )
            : undefined;
    const taken = externalTaken();
    if (taken) {
        return failedPlan(
            call.toolCallId,
            `an EXTERNAL structure already exists ('${taken.name}'); a ` +
                'structure set can have only one'
        );
    }
    return proposal(
        call.toolCallId,
        gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'Structure type',
            summary: `“${name}”: ${fromType || 'unknown'} → ${type}`,
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: () => {
                if (liveRoi(deps, roiNumber)?.isApproved) {
                    return approvedReason(name);
                }
                const t = externalTaken();
                return t
                    ? `an EXTERNAL structure already exists ('${t.name}')`
                    : undefined;
            },
            run: async () => {
                await deps.setType(roiNumber, type);
                await settle();
                if (liveRoi(deps, roiNumber)?.type !== type) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not change the type',
                    };
                }
                return {
                    status: 'applied',
                    roiNumber,
                    roi: name,
                    summary: `'${name}' is now ${type}`,
                };
            },
        })
    );
}
